import type { Home } from '@lmc/ts-common'
import { Text } from '@lmc/ui-kit/src/general'
import { Divider, Popover } from 'antd'
import { getNominative } from 'common/utils'
import { NAMESPACE } from 'locale'
import { accessControlConstants } from 'locale/accessControl'
import { type ReactNode, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

interface AccessControlPopoverProps {
  children: ReactNode
}

const AccessControlPopover = ({ children }: AccessControlPopoverProps) => {
  const { t } = useTranslation(NAMESPACE.ACCESS_CONTROL)

  const [isOpen, setIsOpen] = useState(false)

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )
  const peopleNominative = getNominative(preferredPeopleNominative, 'Residents')

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen)
  }
  return (
    <Popover
      placement="bottom"
      showArrow
      arrow={{ pointAtCenter: true }}
      trigger="hover"
      open={isOpen}
      onOpenChange={handleOpenChange}
      content={
        <Container>
          <Header>
            <Text weight="bold">
              {t(accessControlConstants.WHAT_IS_ACCESS_CONTROL, {
                nominative: peopleNominative
              })}
            </Text>
            <StyledDivider />
          </Header>
          <Content>
            <Text>{t(accessControlConstants.WHAT_IT_DOES)}</Text>
            <BulletList>
              <BulletItem>
                <Text>
                  <Trans
                    i18nKey={accessControlConstants.SHOW_ALL_BULLET}
                    ns={NAMESPACE.ACCESS_CONTROL}
                    values={{ nominative: peopleNominative }}
                    components={{ strong: <strong /> }}
                  />
                </Text>
              </BulletItem>
              <BulletItem>
                <Text>
                  <Trans
                    i18nKey={accessControlConstants.ONLY_ASSIGNED_BULLET}
                    ns={NAMESPACE.ACCESS_CONTROL}
                    values={{ nominative: peopleNominative }}
                    components={{ strong: <strong /> }}
                  />
                </Text>
              </BulletItem>
            </BulletList>
          </Content>
        </Container>
      }
    >
      {children}
    </Popover>
  )
}

export default AccessControlPopover

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 250px;
  color: ${({ theme }) => theme.color.neutral.color3};
`
const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`
const StyledDivider = styled(Divider)`
  margin: 0;
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const BulletList = styled.ul`
  list-style-type: disc;
  margin: 0;
  padding-left: 24px;
`

const BulletItem = styled.li`
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
`
